/**
 * Route-chunk warm-up + the `lazy()`/`Suspense` plumbing every `*.routes.tsx`
 * manifest uses (S3 — "more faster page, quick data handy").
 *
 * Three things live here on purpose, not three files: they all point at the
 * SAME dynamic `import()` specifiers, and keeping them together is what
 * guarantees a prefetch actually primes the chunk a route's `lazy()` will
 * later request — Vite dedupes a chunk by its import specifier, so drift
 * between "what routes.tsx lazy-loads" and "what prefetch.ts warms" would
 * silently produce a SECOND chunk instead of reusing the first.
 *
 *   1. `lazyNamed` — most pages in this repo use a named export
 *      (`export function FooPage()`), not `export default`. `React.lazy`
 *      only accepts a default export, so every route manifest needs this
 *      three-line adapter; centralising it avoids forty copies of the same
 *      `.then(m => ({ default: m.Foo }))`.
 *   2. `RouteSkeleton` — the one Suspense fallback every manifest renders
 *      while its chunk downloads. Deliberately inert (no data, no motion
 *      libraries) so it never itself pulls in a chunk.
 *   3. `prefetchRoute` / `usePrefetchOnIntent` / `useIdlePrefetch` — warm a
 *      route's chunk before the user actually navigates: on hover/focus or
 *      pointerdown for a mouse/pen (desktop has hover; touch does not, so
 *      pointerdown is the equivalent "about to tap" signal), and on idle
 *      after first paint for the couple of screens almost everyone visits
 *      within seconds of landing on `/dashboard`.
 *
 * ADOPTION (no other file needs to change for this to compile and help):
 *
 *   // in the shell's nav item / Link:
 *   import { usePrefetchOnIntent } from "@/lib/prefetch";
 *   <Link to="/records" {...usePrefetchOnIntent("records")}>Records</Link>
 *
 *   // once, near the shell's root (e.g. AppShell's top-level effect):
 *   import { useIdlePrefetch } from "@/lib/prefetch";
 *   useIdlePrefetch(); // warms ["records", "entry"] after first paint
 */
import { createElement, lazy, useEffect, type ComponentType, type ReactElement } from "react";

type Loader = () => Promise<unknown>;

/**
 * `React.lazy` wants `() => Promise<{ default: Component }>`. Most pages here
 * export by name, so this pulls the named export off the resolved module and
 * re-shapes it into the one `lazy()` accepts.
 */
export function lazyNamed<M extends Record<string, unknown>>(
  loader: () => Promise<M>,
  exportName: keyof M & string,
) {
  return lazy(async () => {
    const mod = await loader();
    return { default: mod[exportName] as ComponentType<unknown> };
  });
}

/**
 * Lightweight fallback for every route Suspense boundary. Plain CSS pulse,
 * no chart/PDF/motion import — the whole point is that showing it costs
 * nothing while the real chunk streams in.
 */
export function RouteSkeleton(): ReactElement {
  // Plain `createElement` (not JSX) so this file can stay a `.ts` module —
  // every `*.routes.tsx` manifest that renders this fallback is already
  // `.tsx` and free to use JSX for everything else.
  const bar = (key: number, width: string) =>
    createElement("div", {
      key,
      style: {
        height: "1.1rem",
        width,
        borderRadius: "0.375rem",
        background:
          "linear-gradient(90deg, rgba(148,163,184,0.12) 25%, rgba(148,163,184,0.24) 37%, rgba(148,163,184,0.12) 63%)",
        backgroundSize: "400% 100%",
        animation: "aim-route-skeleton 1.4s ease infinite",
      },
    });

  return createElement(
    "div",
    {
      role: "status",
      "aria-label": "Loading",
      style: { padding: "1.5rem", display: "grid", gap: "0.75rem" },
    },
    bar(0, "100%"),
    bar(1, "100%"),
    bar(2, "40%"),
    createElement(
      "style",
      null,
      "@keyframes aim-route-skeleton { 0% { background-position: 100% 50%; } 100% { background-position: 0% 50%; } }",
    ),
  );
}

/**
 * Registry of "next-likely" chunks, keyed by a short intent name rather than
 * a raw path — several nav destinations (e.g. records) are reachable from
 * more than one place, and a stable name is what `usePrefetchOnIntent` keys
 * off. Add an entry here whenever a new route is worth warming; nothing
 * elsewhere needs to change for the addition to take effect.
 */
const ROUTE_LOADERS: Record<string, Loader> = {
  dashboard: () => import("@/pages/DashboardPage"),
  records: () => import("@/pages/records/RecordsPage"),
  income: () => import("@/pages/income/IncomePage"),
  expenses: () => import("@/pages/expenses/ExpensesPage"),
  accounts: () => import("@/pages/accounts/AccountsPage"),
  analytics: () => import("@/pages/analytics/AnalyticsPage"),
  itr: () => import("@/pages/itr/ITRPage"),
  rent: () => import("@/pages/rent/index/RentDashboardPage"),
  // The wallet quick-add sheet — not a routed page (it overlays the current
  // screen), but the single most-tapped surface after landing on the
  // dashboard, so it earns a spot in the idle warm-up list below.
  entry: () => import("@/wallet/entry/WalletEntry"),
};

const warmed = new Set<string>();

/** Fire-and-forget: prime a chunk without blocking or throwing on the caller. */
export function prefetchRoute(name: string): void {
  if (warmed.has(name)) return;
  const loader = ROUTE_LOADERS[name];
  if (!loader) return;
  warmed.add(name);
  loader().catch(() => {
    // Offline / flaky network: let the real navigation's own lazy() import
    // retry later rather than surfacing anything here.
    warmed.delete(name);
  });
}

/**
 * Spread onto a nav `<Link>`/`<a>` to warm its chunk on desktop hover or
 * keyboard focus, and on `pointerdown` for touch — which fires well before
 * the click/navigation completes, since touch devices have no hover.
 */
export function usePrefetchOnIntent(name: string): {
  onPointerEnter: () => void;
  onPointerDown: () => void;
  onFocus: () => void;
} {
  const handler = () => prefetchRoute(name);
  return { onPointerEnter: handler, onPointerDown: handler, onFocus: handler };
}

/**
 * Warm a fixed set of likely-next chunks once the browser is idle after first
 * paint. `requestIdleCallback` is unavailable on Safari/iOS, which is exactly
 * where this matters most (phones on the family's data plans), hence the
 * timeout fallback.
 */
export function schedulePrefetchOnIdle(names: string[] = ["records", "entry"]): void {
  const run = () => names.forEach(prefetchRoute);
  const w = window as Window & { requestIdleCallback?: (cb: () => void) => number };
  if (typeof w.requestIdleCallback === "function") {
    w.requestIdleCallback(run);
  } else {
    setTimeout(run, 1500);
  }
}

/** Hook form of `schedulePrefetchOnIdle`, for a one-line effect in the shell. */
export function useIdlePrefetch(names: string[] = ["records", "entry"]): void {
  useEffect(() => {
    schedulePrefetchOnIdle(names);
    // Intentionally fires once per mount — `names` is a literal call-site
    // array in every real caller, not derived state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
